import React from 'react';
import { X, Loader2, Newspaper, MessageSquare, Trophy, Calendar, Clock, ExternalLink, Info, ChevronLeft, ChevronRight } from 'lucide-react';
import { at } from '../../../i18nAdmin';
import { Language, User } from '../../../types';

interface UserAuditModalProps {
    isOpen: boolean;
    isAnimating: boolean;
    isLoading: boolean;
    selectedUser: User | null;
    auditTab: 'news' | 'news_comments' | 'match_comments';
    setAuditTab: (tab: 'news' | 'news_comments' | 'match_comments') => void;
    auditStats: { news: number, news_comments: number, match_comments: number };
    auditDateFilter: string;
    setAuditDateFilter: (date: string) => void;
    auditPageSize: number;
    setAuditPageSize: (size: number) => void;
    auditPage: number;
    setAuditPage: (page: number | ((p: number) => number)) => void;
    totalAuditPages: number;
    paginatedAuditItems: any[];
    activeAuditList: any[];
    onClose: () => void;
    lang: Language;
}

export const AdminUserAuditModal: React.FC<UserAuditModalProps> = ({
    isOpen, isAnimating, isLoading, selectedUser, auditTab, setAuditTab, auditStats,
    auditDateFilter, setAuditDateFilter, auditPageSize, setAuditPageSize,
    auditPage, setAuditPage, totalAuditPages, paginatedAuditItems, activeAuditList,
    onClose, lang
}) => {
    if (!isOpen || !selectedUser) return null;

    return (
        <div className={`fixed inset-0 z-[400] flex flex-col justify-end md:items-center md:justify-center transition-opacity duration-500 ${isAnimating ? 'opacity-100' : 'opacity-0'}`}>
            <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose}></div>
            <div className={`relative bg-white w-full max-w-4xl rounded-t-[40px] md:rounded-[40px] shadow-2xl flex flex-col overflow-hidden h-[90vh] md:h-[85vh] transition-transform duration-700 ease-[0.32,0.72,0,1] ${isAnimating ? 'translate-y-0' : 'translate-y-full'}`}>
                <header className="p-8 border-b border-slate-100 flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-slate-100 shadow-sm shrink-0">
                            <img src={selectedUser.avatar || `https://ui-avatars.com/api/?name=${selectedUser.name}`} className="w-full h-full object-cover" />
                        </div>
                        <div className="min-w-0">
                            <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight leading-none mb-1 truncate">{selectedUser.name}</h3>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{at(lang, 'personnelActivityLogs')}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 text-slate-300 md:hover:text-slate-900 transition-colors shrink-0"><X size={24} /></button>
                </header>
                
                <div className="bg-slate-50/50 border-b border-slate-100 px-8 py-4 flex flex-col md:flex-row items-center justify-between gap-6">
                    <div className="flex bg-white p-1 rounded-2xl border border-slate-200 shadow-sm w-full md:w-auto">
                        {[
                            { id: 'news', label: at(lang, 'news'), count: auditStats.news, icon: Newspaper },
                            { id: 'news_comments', label: at(lang, 'newsTalk'), count: auditStats.news_comments, icon: MessageSquare },
                            { id: 'match_comments', label: at(lang, 'matchTalk'), count: auditStats.match_comments, icon: Trophy }
                        ].map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => { setAuditTab(tab.id as any); setAuditPage(1); }}
                                className={`flex-1 md:flex-initial flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                                    auditTab === tab.id 
                                    ? 'bg-slate-900 text-white shadow-md' 
                                    : 'text-slate-400 md:hover:text-slate-600'
                                }`}
                            >
                                <tab.icon size={14} />
                                <span className="hidden sm:inline">{tab.label}</span>
                                <span className={`px-1.5 py-0.5 rounded-md text-[8px] font-black ${auditTab === tab.id ? 'bg-rose-500 text-white' : 'bg-slate-100 text-slate-500'}`}>{tab.count}</span>
                            </button>
                        ))}
                    </div>

                    <div className="flex items-center gap-4 w-full md:w-auto">
                        <div className="relative flex-1 md:w-48 group">
                            <Calendar className="absolute left-3.5 top-1/2 -translate-y-1/2 text-rose-500" size={14} />
                            <input 
                                type="date" 
                                className="w-full bg-white border border-slate-200 rounded-xl py-2.5 pl-10 pr-4 text-[10px] font-black uppercase text-slate-700 outline-none shadow-sm cursor-pointer focus:border-rose-500 transition-colors"
                                value={auditDateFilter}
                                onChange={e => { setAuditDateFilter(e.target.value); setAuditPage(1); }}
                            />
                            {auditDateFilter && (
                                <button onClick={() => setAuditDateFilter('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-300 md:hover:text-rose-500"><X size={12}/></button>
                            )}
                        </div>
                        <div className="flex items-center gap-3">
                            <select 
                                value={auditPageSize}
                                onChange={(e) => { setAuditPageSize(Number(e.target.value)); setAuditPage(1); }}
                                className="bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-[10px] font-black uppercase tracking-widest outline-none shadow-sm cursor-pointer"
                            >
                                <option value={10}>10 {at(lang, 'items')}</option>
                                <option value={30}>30 {at(lang, 'items')}</option>
                                <option value={50}>50 {at(lang, 'items')}</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
                    {isLoading ? (
                        <div className="h-full flex flex-col items-center justify-center gap-4">
                            <Loader2 className="animate-spin text-rose-500" size={32} />
                            <span className="text-[10px] font-black text-slate-300 uppercase tracking-widest animate-pulse">{at(lang, 'syncingLogs')}</span>
                        </div>
                    ) : paginatedAuditItems.length > 0 ? (
                        <div className="space-y-4 animate-in fade-in duration-500">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2 px-2">{at(lang, 'allActivities')} : {at(lang, 'foundCount').replace('{{count}}', activeAuditList.length.toString())}</p>
                            {paginatedAuditItems.map((item, i) => (
                                <div key={i} className="p-5 bg-white border border-slate-100 rounded-3xl flex items-start gap-4 md:hover:shadow-md md:hover:border-slate-200 transition-all group">
                                    <div className="p-2.5 bg-slate-50 rounded-2xl text-slate-300 md:group-hover:text-rose-500 md:group-hover:bg-rose-50 transition-colors shrink-0">
                                        {auditTab === 'news' ? <Newspaper size={18}/> : auditTab === 'news_comments' ? <MessageSquare size={18}/> : <Trophy size={18}/>}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-bold text-slate-700 leading-relaxed mb-2 uppercase tracking-tight">
                                            {auditTab === 'news' ? item.title : item.content}
                                        </p>
                                        <div className="flex items-center gap-3">
                                            <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest tabular-nums flex items-center gap-1.5">
                                                <Clock size={10} className="text-rose-400" />
                                                {new Date(item.created_at || item.published_at).toLocaleString()}
                                            </span>
                                            {auditTab === 'news' && item.type && (
                                                <span className="text-[8px] font-black px-2 py-0.5 bg-slate-100 text-slate-400 rounded-md uppercase tracking-tighter">{item.type}</span>
                                            )}
                                        </div>
                                    </div>
                                    <button 
                                        onClick={() => {
                                            const path = auditTab === 'news' || auditTab === 'news_comments' ? `/feed/item/${item.id || item.news_id}` : `/game/${item.post_id}`;
                                            window.open(`${window.location.origin}${path}`, '_blank');
                                        }}
                                        className="p-2.5 text-slate-200 md:hover:text-indigo-500 md:hover:bg-indigo-50 rounded-xl transition-all shrink-0"
                                    >
                                        <ExternalLink size={18} strokeWidth={2.5} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="h-full flex flex-col items-center justify-center text-center opacity-30 gap-4">
                            <div className="p-6 bg-slate-50 rounded-full">
                                <Info size={48} />
                            </div>
                            <p className="text-sm font-black uppercase tracking-widest">{at(lang, 'noActivityLogs')}</p>
                        </div>
                    )}
                </div>

                <footer className="p-6 border-t border-slate-100 bg-slate-50/50 flex flex-col sm:flex-row items-center justify-between gap-4 shrink-0">
                    <div className="flex items-center gap-4">
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{at(lang, 'page')} {auditPage} {at(lang, 'of')} {totalAuditPages || 1}</span>
                        <div className="flex items-center gap-2">
                            <button 
                                disabled={auditPage === 1}
                                onClick={() => setAuditPage(p => p - 1)}
                                className="p-2 bg-white border border-slate-200 rounded-xl text-slate-400 md:hover:text-slate-900 disabled:opacity-20 transition-all active:scale-90 shadow-sm"
                            >
                                <ChevronLeft size={16} />
                            </button>
                            <button 
                                disabled={auditPage === totalAuditPages || totalAuditPages === 0}
                                onClick={() => setAuditPage(p => p + 1)}
                                className="p-2 bg-white border border-slate-200 rounded-xl text-slate-400 md:hover:text-slate-900 disabled:opacity-20 transition-all active:scale-90 shadow-sm"
                            >
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                </footer>
            </div>
        </div>
    );
};
